import { useCallback, useEffect, useRef, useState } from 'react';
// @mui
import { alpha, styled, useTheme } from '@mui/material/styles';
import {
  Box,
  Stack,
  Paper,
  Popper,
  Fade,
  InputBase,
  Typography,
  IconButton,
  CircularProgress,
  ClickAwayListener,
  useMediaQuery,
} from '@mui/material';
// utils
import { toast } from 'react-hot-toast';
import api, { isSessionExpiredError } from '../../../api';
import Iconify from '../../../components/iconify';
import PackageDetailModal from '../../../components/package-detail-modal';
import { canViewPaymentDetails } from '../../../utils/roleAccess';

// ----------------------------------------------------------------------

const SEARCH_MIN_CHARS = 3;
const DEBOUNCE_MS = 320;

const SearchShell = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: 480,
  margin: '0 auto',
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
  },
}));

const SearchField = styled('div', {
  shouldForwardProp: (prop) => prop !== 'focused',
})(({ theme, focused }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  height: 44,
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(1),
  borderRadius: 14,
  transition: theme.transitions.create(
    ['box-shadow', 'background-color', 'border-color', 'transform', 'max-width'],
    { duration: 220, easing: theme.transitions.easing.easeInOut }
  ),
  backgroundColor: focused
    ? alpha(theme.palette.background.paper, 0.98)
    : alpha(theme.palette.grey[500], 0.08),
  border: `1px solid ${
    focused ? alpha(theme.palette.primary.main, 0.35) : alpha(theme.palette.grey[500], 0.14)
  }`,
  boxShadow: focused
    ? `0 10px 28px ${alpha(theme.palette.primary.main, 0.12)}, 0 0 0 3px ${alpha(
        theme.palette.primary.main,
        0.08
      )}`
    : 'none',
  transform: focused ? 'translateY(-1px)' : 'none',
  '&:hover': {
    backgroundColor: focused
      ? alpha(theme.palette.background.paper, 0.98)
      : alpha(theme.palette.grey[500], 0.12),
  },
}));

const ResultItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.25, 1.5),
  borderRadius: 12,
  cursor: 'pointer',
  transition: theme.transitions.create(['background-color'], { duration: 160 }),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
}));

const MobileOverlay = styled(Box)(({ theme }) => ({
  position: 'fixed',
  inset: 0,
  zIndex: theme.zIndex.modal + 1,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: alpha(theme.palette.background.default, 0.72),
  backdropFilter: 'blur(18px)',
  WebkitBackdropFilter: 'blur(18px)',
}));

// ----------------------------------------------------------------------

const formatPaymentType = (value) => (value === 'Without To Pay' ? 'Credit' : value);

export default function Searchbar() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const anchorRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);

  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [searched, setSearched] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedConsignment, setSelectedConsignment] = useState('');

  const trimmedQuery = query.trim();
  const panelOpen =
    (focused || mobileOpen) && (loading || searched) && trimmedQuery.length >= SEARCH_MIN_CHARS;

  const clearDebounce = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  };

  const openDetail = (consignmentNo) => {
    if (!consignmentNo) return;
    setSelectedConsignment(String(consignmentNo));
    setDetailOpen(true);
    setFocused(false);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const runSearch = useCallback(async (value) => {
    const trimmed = value.trim();
    if (trimmed.length < SEARCH_MIN_CHARS) {
      setResult(null);
      setSearched(false);
      setLoading(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);

    try {
      const encoded = encodeURIComponent(trimmed);
      const response = await api.get(`/api/v1/packages/by_consignment/${encoded}`);

      if (requestId !== requestIdRef.current) {
        return;
      }

      if (response.data?.success && response.data.package) {
        setResult({
          ...response.data.package,
          tracking: response.data.tracking || null,
        });
      } else {
        setResult(null);
      }
      setSearched(true);
    } catch (error) {
      if (requestId !== requestIdRef.current || isSessionExpiredError(error)) {
        return;
      }
      setResult(null);
      setSearched(true);
      const message = error?.response?.data?.message;
      if (error?.response?.status === 403 && message) {
        toast.error(message);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const scheduleSearch = useCallback(
    (value) => {
      clearDebounce();
      const trimmed = value.trim();

      if (trimmed.length < SEARCH_MIN_CHARS) {
        setResult(null);
        setSearched(false);
        setLoading(false);
        return;
      }

      setLoading(true);
      debounceRef.current = setTimeout(() => {
        runSearch(value);
      }, DEBOUNCE_MS);
    },
    [runSearch]
  );

  useEffect(() => () => clearDebounce(), []);

  useEffect(() => {
    if (mobileOpen) {
      const timer = setTimeout(() => inputRef.current?.focus(), 80);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [mobileOpen]);

  const closeMobile = () => {
    setMobileOpen(false);
    setFocused(false);
  };

  const resetSearch = () => {
    clearDebounce();
    setQuery('');
    setResult(null);
    setSearched(false);
    setLoading(false);
  };

  const handleChange = (event) => {
    const { value } = event.target;
    setQuery(value);
    scheduleSearch(value);
  };

  const handleSubmit = () => {
    const consignmentNo = result?.consignmentNo || trimmedQuery;
    if (consignmentNo.length < SEARCH_MIN_CHARS) return;
    openDetail(consignmentNo);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      if (mobileOpen) {
        closeMobile();
      } else {
        setFocused(false);
        inputRef.current?.blur();
      }
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      handleSubmit();
    }
  };

  const handleClickAway = () => {
    if (!isMobile) {
      setFocused(false);
    }
  };

  const trackingStatus = result?.tracking?.currentStatus;
  const isDelivered = Boolean(result?.deliveredAt) || String(trackingStatus || '').toUpperCase() === 'DELIVERED';

  const renderResults = () => (
    <Paper
      elevation={0}
      sx={{
        mt: 1,
        overflow: 'hidden',
        borderRadius: 2.5,
        border: `1px solid ${alpha(theme.palette.grey[500], 0.14)}`,
        boxShadow: `0 18px 40px ${alpha(theme.palette.grey[900], 0.14)}`,
        bgcolor: alpha(theme.palette.background.paper, 0.96),
        backdropFilter: 'blur(16px)',
      }}
    >
      <Box sx={{ px: 1.75, pt: 1.5, pb: 0.75 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 0.4 }}>
          {loading ? 'Looking up consignment…' : `Consignment “${trimmedQuery}”`}
        </Typography>
      </Box>

      <Box sx={{ px: 1, pb: 1 }}>
        {loading && !result ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
            <CircularProgress size={22} thickness={4} />
          </Stack>
        ) : null}

        {!loading && searched && !result ? (
          <Stack alignItems="center" spacing={1} sx={{ py: 4, px: 2 }}>
            <Iconify icon="solar:box-minimalistic-bold-duotone" width={36} sx={{ color: 'text.disabled' }} />
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
              No package found
            </Typography>
          </Stack>
        ) : null}

        {!loading && result ? (
          <ResultItem
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => openDetail(result.consignmentNo)}
          >
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 1.5,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
              }}
            >
              <Iconify icon="mdi:package-variant-closed" width={20} />
            </Box>
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
                {result.consignmentNo}
              </Typography>
              <Typography variant="caption" noWrap sx={{ color: 'text.secondary', display: 'block' }}>
                {result.senderName || 'Unknown sender'}
                {result.consigneePinCode ? ` · ${result.consigneePinCode}` : ''}
              </Typography>
              <Typography variant="caption" noWrap sx={{ color: 'text.disabled', display: 'block' }}>
                {canViewPaymentDetails(result) ? (
                  <>
                    {formatPaymentType(result.paymentType) || '—'}
                    {' · '}
                    ₹{Number(result.totalAmount || 0).toLocaleString('en-IN')}
                    {isDelivered ? ' · Delivered' : trackingStatus ? ` · ${trackingStatus}` : ''}
                  </>
                ) : (
                  <>
                    {trackingStatus || (isDelivered ? 'Delivered' : 'Package')}
                    {result.consigneeAddress ? ` · ${String(result.consigneeAddress).slice(0, 36)}` : ''}
                  </>
                )}
              </Typography>
            </Box>
            <Iconify
              icon="eva:arrow-ios-forward-fill"
              width={16}
              sx={{ color: 'text.disabled', mt: 1, flexShrink: 0 }}
            />
          </ResultItem>
        ) : null}
      </Box>
    </Paper>
  );

  const searchInput = (
    <SearchField focused={focused || mobileOpen}>
      <Iconify icon="eva:search-fill" width={20} sx={{ color: 'text.disabled', flexShrink: 0 }} />
      <InputBase
        inputRef={inputRef}
        fullWidth
        value={query}
        onChange={handleChange}
        onFocus={() => setFocused(true)}
        onKeyDown={handleKeyDown}
        placeholder="Search consignment no…"
        inputProps={{ 'aria-label': 'Search consignment number' }}
        sx={{
          typography: 'body2',
          '& input': {
            py: 1,
            fontWeight: 500,
            letterSpacing: 0.2,
          },
        }}
      />
      {loading ? (
        <CircularProgress size={16} thickness={5} sx={{ mr: 0.5 }} />
      ) : query ? (
        <IconButton
          size="small"
          onClick={() => {
            resetSearch();
            inputRef.current?.focus();
          }}
          sx={{ color: 'text.disabled' }}
        >
          <Iconify icon="eva:close-fill" width={16} />
        </IconButton>
      ) : null}
    </SearchField>
  );

  return (
    <>
      {!isMobile && (
        <ClickAwayListener onClickAway={handleClickAway}>
          <SearchShell ref={anchorRef}>
            {searchInput}
            <Popper
              open={panelOpen}
              anchorEl={anchorRef.current}
              placement="bottom-start"
              transition
              disablePortal
              style={{ width: anchorRef.current?.offsetWidth || undefined, zIndex: theme.zIndex.modal }}
            >
              {({ TransitionProps }) => (
                <Fade {...TransitionProps} timeout={180}>
                  <Box>{renderResults()}</Box>
                </Fade>
              )}
            </Popper>
          </SearchShell>
        </ClickAwayListener>
      )}

      {isMobile && (
        <>
          <IconButton
            color={mobileOpen ? 'primary' : 'default'}
            onClick={() => setMobileOpen(true)}
            sx={{
              width: 40,
              height: 40,
              bgcolor: (t) => alpha(t.palette.grey[500], 0.08),
              '&:hover': { bgcolor: (t) => alpha(t.palette.grey[500], 0.14) },
            }}
          >
            <Iconify icon="eva:search-fill" />
          </IconButton>

          <Fade in={mobileOpen} timeout={220} unmountOnExit>
            <MobileOverlay>
              <Box sx={{ px: 2, pt: `calc(${theme.spacing(1.5)} + env(safe-area-inset-top))`, pb: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <IconButton onClick={closeMobile} sx={{ color: 'text.primary' }}>
                    <Iconify icon="eva:arrow-ios-back-fill" />
                  </IconButton>
                  <Box sx={{ flexGrow: 1 }}>{searchInput}</Box>
                </Stack>
              </Box>
              <Box sx={{ px: 2, flexGrow: 1, overflowY: 'auto' }}>
                <Fade in={panelOpen} timeout={200}>
                  <Box>{panelOpen ? renderResults() : null}</Box>
                </Fade>
              </Box>
            </MobileOverlay>
          </Fade>
        </>
      )}

      <PackageDetailModal
        open={detailOpen}
        consignmentNo={selectedConsignment}
        onClose={() => {
          setDetailOpen(false);
          setTimeout(() => setSelectedConsignment(''), 280);
        }}
      />
    </>
  );
}
